import { z } from 'zod';

import type { RequestListItem } from './api';

const SETTINGS_KEY = 'deffy-direct:admin-notifications:v1';
const DELIVERY_KEY = 'deffy-direct:admin-notification-delivery:v1';

const NotificationSettingsSchema = z.object({
  browserEnabled: z.boolean(),
  soundEnabled: z.boolean(),
  reducedSensory: z.boolean(),
  volume: z.number().min(0).max(1),
  sound: z.enum(['cosmic_chime', 'soft_ping']),
  escalationMinutes: z
    .array(z.number().int().min(1).max(240))
    .max(5)
    .transform((values) => [...new Set(values)].sort((a, b) => a - b)),
});

const DeliveryStateSchema = z.record(z.string(), z.string().datetime());

export type NotificationSound = 'cosmic_chime' | 'soft_ping';
export type AdminNotificationSettings = z.input<typeof NotificationSettingsSchema>;

export interface DueAdminAlert {
  key: string;
  request: RequestListItem;
  reason: 'new_request' | 'pending_addition' | 'escalation';
  escalationMinutes: number | null;
}

export function defaultAdminNotificationSettings(): AdminNotificationSettings {
  return {
    browserEnabled: false,
    soundEnabled: false,
    reducedSensory:
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    volume: 0.32,
    sound: 'cosmic_chime',
    escalationMinutes: [5, 15, 30],
  };
}

export function loadAdminNotificationSettings(): AdminNotificationSettings {
  const fallback = defaultAdminNotificationSettings();
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    return raw ? NotificationSettingsSchema.parse(JSON.parse(raw)) : fallback;
  } catch {
    return fallback;
  }
}

export function saveAdminNotificationSettings(
  settings: AdminNotificationSettings,
): AdminNotificationSettings {
  const validated = NotificationSettingsSchema.parse(settings);
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(validated));
  return validated;
}

function loadDeliveryState(): Record<string, string> {
  try {
    const raw = localStorage.getItem(DELIVERY_KEY);
    return raw ? DeliveryStateSchema.parse(JSON.parse(raw)) : {};
  } catch {
    return {};
  }
}

function saveDeliveryState(state: Record<string, string>): void {
  const recent = Object.entries(state)
    .sort((left, right) => Date.parse(right[1]) - Date.parse(left[1]))
    .slice(0, 500);
  localStorage.setItem(DELIVERY_KEY, JSON.stringify(Object.fromEntries(recent)));
}

export function collectDueAdminAlerts(
  requests: RequestListItem[],
  settings: AdminNotificationSettings,
  delivered: Readonly<Record<string, string>>,
  now = Date.now(),
): DueAdminAlert[] {
  const validated = NotificationSettingsSchema.parse(settings);
  const due: DueAdminAlert[] = [];
  for (const request of requests) {
    const candidates: DueAdminAlert[] = [];
    if (request.state === 'pending' || request.state === 'transmitting') {
      candidates.push({
        key: `${request.publicId}:new`,
        request,
        reason: 'new_request',
        escalationMinutes: null,
      });
      const waitingMinutes = Math.max(0, (now - Date.parse(request.waitingSince)) / 60_000);
      for (const minutes of validated.escalationMinutes) {
        if (waitingMinutes >= minutes) {
          candidates.push({
            key: `${request.publicId}:wait:${minutes}`,
            request,
            reason: 'escalation',
            escalationMinutes: minutes,
          });
        }
      }
    }
    if (request.pendingAdditionCount > 0) {
      candidates.push({
        key: `${request.publicId}:addition:${request.pendingAdditionCount}`,
        request,
        reason: 'pending_addition',
        escalationMinutes: null,
      });
    }
    const newestUndelivered = candidates.filter((candidate) => !delivered[candidate.key]).at(-1);
    if (newestUndelivered) due.push(newestUndelivered);
  }
  return due;
}

let audioContext: AudioContext | null = null;

export async function primeNotificationSound(): Promise<boolean> {
  if (!('AudioContext' in window)) return false;
  audioContext ??= new AudioContext();
  await audioContext.resume();
  return audioContext.state === 'running';
}

function playTone(
  context: AudioContext,
  frequency: number,
  start: number,
  duration: number,
  volume: number,
  wave: OscillatorType,
): void {
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.type = wave;
  oscillator.frequency.setValueAtTime(frequency, start);
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, volume), start + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  oscillator.connect(gain).connect(context.destination);
  oscillator.start(start);
  oscillator.stop(start + duration + 0.02);
}

function playNotificationSound(settings: AdminNotificationSettings): void {
  if (
    !settings.soundEnabled ||
    settings.reducedSensory ||
    !audioContext ||
    audioContext.state !== 'running'
  )
    return;
  const start = audioContext.currentTime + 0.01;
  if (settings.sound === 'soft_ping') {
    playTone(audioContext, 680, start, 0.16, settings.volume * 0.08, 'sine');
    return;
  }
  playTone(audioContext, 440, start, 0.18, settings.volume * 0.06, 'sine');
  playTone(audioContext, 660, start + 0.09, 0.22, settings.volume * 0.07, 'triangle');
}

function alertBody(alert: DueAdminAlert): string {
  const context = `${alert.request.intent ?? 'No intent selected'} · ${alert.request.urgency} · ${alert.request.sourceProject.label}`;
  if (alert.reason === 'pending_addition') {
    return `${alert.request.pendingAdditionCount} pending addition${alert.request.pendingAdditionCount === 1 ? '' : 's'}. ${context}`;
  }
  if (alert.reason === 'escalation') {
    return `Waiting at least ${alert.escalationMinutes ?? 0} minutes. ${context}`;
  }
  return `${alert.request.firstMessageExcerpt}\n${context}`;
}

export function deliverAdminAlerts(
  alerts: DueAdminAlert[],
  settings: AdminNotificationSettings,
  onActivate: (requestPublicId: string) => void,
): number {
  const canNotify =
    settings.browserEnabled && 'Notification' in window && Notification.permission === 'granted';
  const canSound =
    settings.soundEnabled && !settings.reducedSensory && audioContext?.state === 'running';
  if (!canNotify && !canSound) return 0;

  const delivered = loadDeliveryState();
  const timestamp = new Date().toISOString();
  for (const alert of alerts) {
    delivered[alert.key] = timestamp;
    if (canNotify) {
      const notification = new Notification(`${alert.request.alias} sent a signal`, {
        body: alertBody(alert),
        icon: '/pwa-192.svg',
        tag: alert.key,
        silent: true,
      });
      notification.onclick = () => {
        window.focus();
        onActivate(alert.request.publicId);
        notification.close();
      };
    }
    playNotificationSound(settings);
  }
  saveDeliveryState(delivered);
  return alerts.length;
}

export function evaluateAndDeliverAdminAlerts(
  requests: RequestListItem[],
  settings: AdminNotificationSettings,
  onActivate: (requestPublicId: string) => void,
): number {
  const delivered = loadDeliveryState();
  return deliverAdminAlerts(
    collectDueAdminAlerts(requests, settings, delivered),
    settings,
    onActivate,
  );
}

export function notificationPermission(): NotificationPermission | 'unsupported' {
  return 'Notification' in window ? Notification.permission : 'unsupported';
}

export async function requestNotificationPermission(): Promise<
  NotificationPermission | 'unsupported'
> {
  return 'Notification' in window
    ? Notification.requestPermission()
    : Promise.resolve('unsupported');
}

export function sendAdminNotificationTest(settings: AdminNotificationSettings): boolean {
  const canNotify =
    settings.browserEnabled && 'Notification' in window && Notification.permission === 'granted';
  const canSound =
    settings.soundEnabled && !settings.reducedSensory && audioContext?.state === 'running';
  if (canNotify) {
    const notification = new Notification('Deffy Direct test signal', {
      body: 'Browser alerts are connected to this private console.',
      icon: '/pwa-192.svg',
      tag: 'deffy-direct-test',
      silent: true,
    });
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  }
  playNotificationSound(settings);
  return Boolean(canNotify || canSound);
}
